// Vite resolves import.meta.glob at build time relative to the file
// that contains it. This file lives at web/src/features/onboarding/manifest.ts
// so three levels up lands at web/. From there '../..' is the repo root,
// and '../../documentation/client-onboarding' is the docs folder.
//
// Vite's default fs.allow permits the project root and above by default;
// the glob path is validated at build time, not runtime.
//
// When a new NN-foo.md is added to documentation/client-onboarding/ the
// next build automatically picks it up — no code changes required.

import type { DocEntry } from './types'

// import.meta.glob with { query: '?raw', import: 'default', eager: true }
// inlines each file as a string at build time. The Record key is the
// full resolved path (e.g. "/absolute/path/to/01-tenant-setup.md").
// import.meta.glob with eager:true and ?raw returns Record<string, string>
// at runtime. The TypeScript type from vite/client is broader (unknown),
// so we narrow via a type guard rather than a cast.
const rawDocsUnknown = import.meta.glob(
  '../../../../documentation/client-onboarding/*.md',
  { query: '?raw', import: 'default', eager: true },
)

const rawExamplesUnknown = import.meta.glob(
  '../../../../documentation/client-onboarding/examples/*.md',
  { query: '?raw', import: 'default', eager: true },
)

function isStringRecord(v: Record<string, unknown>): v is Record<string, string> {
  return Object.values(v).every((val) => typeof val === 'string')
}

const rawDocs: Record<string, string> = isStringRecord(rawDocsUnknown)
  ? rawDocsUnknown
  : {}

const rawExamples: Record<string, string> = isStringRecord(rawExamplesUnknown)
  ? rawExamplesUnknown
  : {}

/** Postman download URLs — Vite copies the file into dist/assets/ with
 *  a content-hash. Import with ?url so the bundler handles asset hashing. */
export { default as postmanCollectionUrl } from '../../../../documentation/client-onboarding/postman/antifraud-rest-api.postman_collection.json?url'
export { default as postmanStagingUrl } from '../../../../documentation/client-onboarding/postman/antifraud-staging.postman_environment.json?url'
export { default as postmanProductionUrl } from '../../../../documentation/client-onboarding/postman/antifraud-production.postman_environment.json?url'

/** Extract the first H1 heading from a markdown body. */
function extractTitle(body: string): string {
  const match = /^#\s+(.+)$/m.exec(body)
  return match?.[1]?.trim() ?? 'Untitled'
}

/** Derive slug + order from a filename path like "/foo/bar/01-tenant-setup.md". */
function parseDocFilename(fullPath: string): { slug: string; order: number } {
  const filename = fullPath.split('/').at(-1) ?? ''
  const name = filename.replace(/\.md$/, '')
  // README.md → order 0
  if (name === 'README') return { slug: 'readme', order: 0 }
  // NN-some-name.md → order = NN
  const numbered = /^(\d+)-(.+)$/.exec(name)
  if (numbered) {
    const [, digits, rest] = numbered
    return { slug: `${digits}-${rest}`, order: parseInt(digits ?? '99', 10) }
  }
  // fallback — alphabetic
  return { slug: name.toLowerCase(), order: 999 }
}

function buildEntries(
  raw: Record<string, string>,
  isExample: boolean,
  baseOrder: number,
): DocEntry[] {
  return Object.entries(raw)
    .map(([path, body]) => {
      const { slug, order } = parseDocFilename(path)
      const adjustedOrder = isExample ? baseOrder + order : order
      return { slug, order: adjustedOrder, title: extractTitle(body), body, isExample }
    })
    .sort((a, b) => a.order - b.order)
}

/** All guide docs (README + numbered), sorted by order. */
export const guideDocs: DocEntry[] = buildEntries(rawDocs, false, 0)

/** All example docs (curl, python, nodejs), sorted. */
export const exampleDocs: DocEntry[] = buildEntries(rawExamples, true, 100)

/** Flat map of slug → DocEntry for fast lookup. */
export const docsBySlug: ReadonlyMap<string, DocEntry> = new Map(
  [...guideDocs, ...exampleDocs].map((d) => [d.slug, d]),
)
